import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Modal, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import RegulationAdminDetail from './RegulationAdminDetail';
import { STATE_COLORS, STATE_FILTERS, STATE_LABELS, domainOptions, rowState, topicOptions } from '../../utils/regulatoryAdminModel';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import {
    PAGE_SIZE,
    authorityOptions,
    buildCreateBody,
    buildListQuery,
    describeIngestResult,
    fieldFromError,
    documentTypeLabel,
    documentTypeOptions,
    sinceInvalid,
    validateCreateForm,
} from '../../utils/regulatoryModel';

const { Text } = Typography;

const EMPTY_FORM = { authorityName: undefined, documentType: undefined, officialDocumentNumber: '', title: '', text: '', sourceUrl: '', publishedDate: '', effectiveDate: '', jurisdictionScope: '', topicCodes: [] };

const AddByHandModal = ({ sources, open, onClose, onCreated }) => {
    const { request } = useApiRequest();
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [badField, setBadField] = useState(null);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
    const setEv = (k) => (e) => set(k)(e.target.value);
    const errors = validateCreateForm(form);

    const submit = async () => {
        setBusy(true);
        setServerError(null);
        try {
            const { data } = await request('POST', '/regulatory-content', { body: buildCreateBody(form) });
            message.success('Added (unpublished). Open it to draft a summary with AI.');
            setForm(EMPTY_FORM);
            onCreated(data?.id);
        } catch (err) {
            setServerError(userMessageForError(err));
            setBadField(fieldFromError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open={open} title="Add a regulation by hand" okText="Add regulation" onOk={submit} onCancel={onClose} okButtonProps={{ disabled: errors.length > 0 || busy }} confirmLoading={busy}>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Select aria-label="Authority" style={{ width: '100%' }} placeholder="Issuing authority" value={form.authorityName} onChange={set('authorityName')} options={authorityOptions(sources?.authorities)} />
                <Select aria-label="Document type" style={{ width: '100%' }} placeholder="Document type" value={form.documentType} onChange={set('documentType')} options={documentTypeOptions(sources?.documentTypes)} />
                <Input aria-label="Reference number" status={badField === 'officialDocumentNumber' ? 'error' : undefined} aria-invalid={badField === 'officialDocumentNumber' ? 'true' : undefined} placeholder="Reference number printed on the document" value={form.officialDocumentNumber} onChange={setEv('officialDocumentNumber')} />
                <Input aria-label="Title" status={badField === 'title' ? 'error' : undefined} aria-invalid={badField === 'title' ? 'true' : undefined} placeholder="Title" value={form.title} onChange={setEv('title')} />
                <Input aria-label="Official source link" placeholder="https:// link on the regulator's own site" value={form.sourceUrl} onChange={setEv('sourceUrl')} />
                <Input aria-label="Issue date" placeholder="Issue date (DD-MM-YYYY)" value={form.publishedDate} onChange={setEv('publishedDate')} />
                <Input aria-label="Effective date" placeholder="Effective date (DD-MM-YYYY), optional" value={form.effectiveDate} onChange={setEv('effectiveDate')} />
                <Input aria-label="Jurisdiction scope" placeholder="Jurisdiction scope, comma separated (optional)" value={form.jurisdictionScope} onChange={setEv('jurisdictionScope')} />
                <Select aria-label="Topics" mode="multiple" style={{ width: '100%' }} placeholder="Topics (optional; the AI draft may propose more)" optionFilterProp="label" showSearch value={form.topicCodes} onChange={(v) => set('topicCodes')(v || [])} options={topicOptions(sources?.taxonomy)} />
                <Input.TextArea aria-label="Official text" aria-invalid={badField === 'text' ? 'true' : undefined} rows={8} placeholder="Paste the official text of the document" value={form.text} onChange={setEv('text')} />
            </Space>
            {errors.length > 0 && <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>{errors.map((m) => <li key={m}><Text type="secondary">{m}</Text></li>)}</ul>}
            {serverError && <Alert type="error" style={{ marginTop: 8 }} message={serverError} data-testid="create-error" />}
        </Modal>
    );
};

const statusTag = (r) => {
    if (r.supersededById) return <Tag color="red">Superseded</Tag>;
    const st = rowState(r);
    if (st === 'published') return <Tag color="green" data-testid="state-tag">Published v{r.approvedVersions}</Tag>;
    if (st) return <Tag color={STATE_COLORS[st]} data-testid="state-tag">{STATE_LABELS[st]}</Tag>;
    return <Tag data-testid="state-tag">Unpublished</Tag>;
};

/** The catalogue: filters, ingest, add by hand, and the per-document detail. */
const RegulatoryItemsPanel = ({ sources, rev, onChanged, openId }) => {
    const { request } = useApiRequest();
    const [draftFilters, setDraftFilters] = useState({});
    const [filters, setFilters] = useState({});
    const [offset, setOffset] = useState(0);
    const [selectedId, setSelectedId] = useState(openId || null);
    useEffect(() => { if (openId) setSelectedId(openId); }, [openId]);
    const [addOpen, setAddOpen] = useState(false);
    const [ingesting, setIngesting] = useState(false);
    const [ingest, setIngest] = useState(null); // { results } | { error }

    const list = useResource(
        async () => (await request('GET', '/regulatory-content', { query: buildListQuery(filters, { admin: true, offset }) })),
        [request, filters, offset, rev],
    );

    const set = (k) => (v) => setDraftFilters((f) => ({ ...f, [k]: v }));
    const apply = () => {
        setOffset(0);
        setFilters(draftFilters);
    };

    // Triage chips apply at once and keep the other applied filters.
    const chooseState = (status) => {
        setOffset(0);
        setDraftFilters((f) => ({ ...f, status }));
        setFilters((f) => ({ ...f, status }));
    };

    const runIngest = async () => {
        setIngesting(true);
        setIngest(null);
        try {
            const { data } = await request('POST', '/regulatory-content/ingest', { body: {} });
            setIngest({ results: data?.results || {} });
            await onChanged();
        } catch (err) {
            setIngest({ error: userMessageForError(err) });
        } finally {
            setIngesting(false);
        }
    };

    const items = list.data?.data?.items || [];
    const total = list.data?.meta?.total ?? items.length;
    const sebi = (sources?.fetchableSources || []).find((s) => s.source === 'sebi');
    const sinceBad = sinceInvalid(draftFilters.since);

    const columns = [
        { title: 'Title', key: 'title', render: (_v, r) => (<span><Text>{r.title}</Text>{(r.topics || []).map((t) => <Tag key={t.id}>{t.title}</Tag>)}</span>) },
        { title: 'Authority', key: 'authority', render: (_v, r) => r.authorityName },
        { title: 'Type', key: 'type', render: (_v, r) => documentTypeLabel(r.documentType) },
        { title: 'Reference', key: 'ref', render: (_v, r) => r.officialDocumentNumber || '-' },
        { title: 'Issued', key: 'issued', render: (_v, r) => formatDdMmYyyy(r.publishedAt) },
        {
            title: 'Status',
            key: 'status',
            render: (_v, r) => (
                <Space>
                    {statusTag(r)}
                    {r.dismissedAt && r.dismissedReason && <Text type="secondary" style={{ fontSize: 12 }} data-testid="dismissed-reason">Reason: {r.dismissedReason}</Text>}
                    {r.unpublished && r.unpublishedReason && <Text type="secondary" style={{ fontSize: 12 }} data-testid="unpublished-reason">Reason: {r.unpublishedReason}</Text>}
                    {r.pendingDraftIds?.length > 0 && <Tag color="orange">Draft awaiting review</Tag>}
                </Space>
            ),
        },
        { title: '', key: 'open', render: (_v, r) => <Button onClick={() => setSelectedId(r.id)}>Open</Button> },
    ];

    return (
        <div>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select aria-label="Authority filter" style={{ width: 140 }} placeholder="Authority" allowClear value={draftFilters.authority} onChange={set('authority')} options={authorityOptions(sources?.authorities)} />
                <Select aria-label="Type filter" style={{ width: 180 }} placeholder="Document type" allowClear value={draftFilters.documentType} onChange={set('documentType')} options={documentTypeOptions(sources?.documentTypes)} />
                <Select aria-label="Domain filter" style={{ width: 200 }} placeholder="Domain" allowClear value={draftFilters.domain} onChange={set('domain')} options={domainOptions(sources?.taxonomy)} />
                <Select aria-label="Topic filter" style={{ width: 220 }} placeholder="Topic" allowClear showSearch optionFilterProp="label" value={draftFilters.topic} onChange={set('topic')} options={topicOptions(sources?.taxonomy)} />
                <Input aria-label="Search" style={{ width: 200 }} placeholder="Search title or reference" value={draftFilters.q || ''} onChange={(e) => set('q')(e.target.value)} />
                <Input aria-label="Issued since" style={{ width: 160 }} placeholder="Since (DD-MM-YYYY)" value={draftFilters.since || ''} onChange={(e) => set('since')(e.target.value)} />
                <Checkbox checked={!!draftFilters.includeSuperseded} onChange={(e) => set('includeSuperseded')(e.target.checked)}>Include superseded</Checkbox>
                <Button onClick={apply} disabled={sinceBad}>Apply filters</Button>
            </Space>
            {sinceBad && <div><Text type="danger">Enter the date as DD-MM-YYYY.</Text></div>}

            <Space wrap style={{ marginBottom: 12 }} data-testid="state-chips">
                <Button aria-pressed={!filters.status} onClick={() => chooseState(undefined)}>{!filters.status ? 'All states (selected)' : 'All states'}</Button>
                {STATE_FILTERS.map((s) => (
                    <Button key={s} aria-pressed={filters.status === s} onClick={() => chooseState(s)}>{filters.status === s ? `${STATE_LABELS[s]} (selected)` : STATE_LABELS[s]}</Button>
                ))}
            </Space>

            <Space wrap style={{ marginBottom: 12 }}>
                <Button onClick={runIngest} disabled={ingesting}>{ingesting ? 'Fetching RBI and SEBI feeds...' : 'Fetch RBI and SEBI feeds'}</Button>
                <Button onClick={() => setAddOpen(true)}>Add a regulation by hand</Button>
            </Space>
            {sources && (
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {sebi && !sebi.carriesFullText ? "SEBI's feed carries titles only: paste the official text by hand before drafting. " : ''}
                        {sources.manualOnlyAuthorities?.length ? `${sources.manualOnlyAuthorities.join(', ')} have no feed and are added by hand.` : ''}
                    </Text>
                </div>
            )}
            {ingest?.error && <Alert type="error" style={{ marginBottom: 12 }} message={ingest.error} data-testid="ingest-error" />}
            {ingest?.results && (
                <Alert
                    type={Object.values(ingest.results).some((r) => r.error || r.failed > 0) ? 'warning' : 'success'}
                    style={{ marginBottom: 12 }}
                    closable
                    message="Feed fetch finished"
                    description={<div data-testid="ingest-results">{Object.entries(ingest.results).map(([s, r]) => <div key={s}>{describeIngestResult(s, r)}</div>)}</div>}
                />
            )}

            {list.status === 'loading' && !list.data && <Spin />}
            {list.status === 'error' && <Alert type="error" message={userMessageForError(list.error)} action={<Button onClick={list.reload}>Retry</Button>} />}
            {list.data && items.length === 0 && <Empty description="No documents match these filters. Fetch the feeds or add a regulation by hand." />}
            {list.data && items.length > 0 && (
                <>
                    <Table columns={columns} dataSource={items} rowKey={(r) => r.id} pagination={false} size="small" />
                    <Space style={{ marginTop: 8 }}>
                        <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
                        <Text type="secondary">{offset + 1}-{offset + items.length} of {total}</Text>
                        <Button disabled={offset + items.length >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
                    </Space>
                </>
            )}

            {selectedId && <RegulationAdminDetail key={selectedId} id={selectedId} sources={sources} onClose={() => setSelectedId(null)} onChanged={onChanged} />}
            {addOpen && (
                <AddByHandModal
                    sources={sources}
                    open
                    onClose={() => setAddOpen(false)}
                    onCreated={async (id) => { setAddOpen(false); if (id) setSelectedId(id); await onChanged(); }}
                />
            )}
        </div>
    );
};

export default RegulatoryItemsPanel;
